import { Vehicle } from "@shared/platform-api/generated"
import { Data, platform, SortOrder } from "@shared/platform-api/requests"
import { InsightVehicle } from "@shared/types/types"
import { formatPercentage } from "@shared/utils/formatters/formatPercentage"
import { kmToMileConverterWithDecimals } from "@shared/utils/formatters/kmToMiles"
import { downloadInsightVehiclesAsCsv } from "@shared/utils/helpers/downloadAsCsv"
import { SearchTripVehiclesInput, TripVehicleSortKeysFE } from "../api/insights"
import { calculateScoreChange } from "."

export const buildApiInputAndGetInsightVehicles = async (
  input: SearchTripVehiclesInput,
  limit: number,
  search: string,
  sort: {
    property: TripVehicleSortKeysFE | undefined
    order: SortOrder | undefined
  },
  holder: string,
  policyNumber: string,
  getToken: () => Promise<string>
): Promise<boolean> => {
  const sortKey =
    sort.property === `vehicleScore`
      ? `currentPeriod.riskScore`
      : sort.property === `currentPeriodDistance`
      ? `currentPeriod.distanceKm`
      : `vrn`
  const apiInput: SearchTripVehiclesInput = {
    ...input,
    limit,
    sortKey,
    sortOrder: sort.order,
    term: search,
  }
  const insightsVehicles = await getInsightVehicles(apiInput, getToken)
  if (!insightsVehicles) return false
  downloadInsightVehiclesAsCsv(insightsVehicles, holder, policyNumber)
  return true
}

const mapInsightVehicles = (vehicle: Vehicle): InsightVehicle => {
  const speedBandsPercentage =
    (vehicle.currentPeriod?.minorSpeedBandPercentage || 0) +
    (vehicle.currentPeriod?.majorSpeedBandPercentage || 0) +
    (vehicle.currentPeriod?.criticalSpeedBandPercentage || 0)
  const change = calculateScoreChange(vehicle.currentPeriod?.riskScore, vehicle.previousPeriod?.riskScore)
  const score = vehicle.currentPeriod?.safetyScore ? Math.round(vehicle.currentPeriod?.safetyScore) : null

  return {
    averageSpeedExceedencePercentage: formatPercentage(
      vehicle.currentPeriod?.averageSpeedExceedencePercentage ?? null,
      1
    ),
    distance: kmToMileConverterWithDecimals(vehicle.currentPeriod?.distanceKm, 1),
    distanceSpentSpeedingPercentage: formatPercentage(speedBandsPercentage, 1),
    name: vehicle.name ?? ``,
    score,
    scoreChange: change ? Math.round(-change) : null,
    vrn: vehicle.vrn,
  }
}

const getInsightVehicles = async (
  input: SearchTripVehiclesInput,
  getToken: () => Promise<string>
): Promise<ReadonlyArray<InsightVehicle> | null> => {
  const vehicles: InsightVehicle[] = []
  let lastResult: Data<Vehicle> | null = null
  let apiInput = input
  let error = false

  const api = platform(getToken)
  do {
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await api.trips.searchVehicles(apiInput)
      apiInput = { ...input, after: result.next ?? undefined }
      lastResult = result
      const mappedVehicles = result.items.map((vehicle) => mapInsightVehicles(vehicle))
      vehicles.push(...mappedVehicles)
    } catch (err: any) {
      error = true
    }
  } while (lastResult?.next !== null && !error)

  return error ? null : vehicles
}
